import {
  createContext,
  type FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ApiError,
  apiGet,
  apiPost,
  apiUrl,
  clearBasicAuthCredentials,
  setBasicAuthCredentials,
} from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

interface AuthGateProps {
  children: React.ReactNode;
}

type AuthMode = "unknown" | "none" | "basic" | "oidc" | "both";
type SupportedAuthMode = Exclude<AuthMode, "unknown">;

interface AuthModeResponse {
  mode?: string;
  basicEnabled?: boolean;
  oidcEnabled?: boolean;
}

interface AuthSessionContextValue {
  mode: SupportedAuthMode;
  authenticated: boolean;
  username: string | null;
  logout: () => Promise<void>;
}

const AuthSessionContext = createContext<AuthSessionContextValue | null>(null);

export function useAuthSession(): AuthSessionContextValue {
  const ctx = useContext(AuthSessionContext);
  if (!ctx) {
    throw new Error("useAuthSession must be used within <AuthGate>");
  }
  return ctx;
}

export function AuthGate({ children }: AuthGateProps): JSX.Element {
  const [mode, setMode] = useState<AuthMode>("unknown");
  const [basicEnabled, setBasicEnabled] = useState(false);
  const [oidcEnabled, setOidcEnabled] = useState(false);
  const [authUsername, setAuthUsername] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [bootGeneration, setBootGeneration] = useState(0);

  const retryBoot = useCallback(() => {
    setBootError(null);
    setBootGeneration((current) => current + 1);
  }, []);

  // Boot: read auth mode from backend; if a session might exist (OIDC
  // cookie set by the callback, or basic credentials in sessionStorage),
  // probe /auth/me to confirm. Cookies travel transparently because the
  // API client uses `credentials: "include"`.
  //
  // React StrictMode mounts effects twice in development. We abort
  // in-flight fetches on cleanup and ignore late results so a stale
  // boot pass cannot overwrite a successful one with `mode="none"`.
  useEffect(() => {
    let active = true;
    const abort = new AbortController();

    const boot = async () => {
      setBootError(null);
      setChecking(true);
      try {
        const auth = await apiGet<AuthModeResponse>("/auth/mode", {
          signal: abort.signal,
        });
        if (!active) return;

        const nextMode: AuthMode =
          auth.mode === "basic"
          || auth.mode === "none"
          || auth.mode === "oidc"
          || auth.mode === "both"
            ? auth.mode
            : "none";

        setMode(nextMode);
        setBasicEnabled(Boolean(auth.basicEnabled));
        setOidcEnabled(Boolean(auth.oidcEnabled));

        if (nextMode === "none") {
          setAuthenticated(true);
          setAuthUsername(null);
          return;
        }

        // /auth/me is cheap; just call it. If we have a valid session
        // cookie or Basic header, we get 200; otherwise 401, which we
        // treat as "not signed in" and render the login screen.
        try {
          const me = await apiGet<{ username?: string | null }>("/auth/me", {
            signal: abort.signal,
          });
          if (!active) return;
          setAuthenticated(true);
          setAuthUsername(
            typeof me.username === "string" && me.username ? me.username : null,
          );
        } catch (authProbeError) {
          if (!active || abort.signal.aborted) return;
          if (
            authProbeError instanceof ApiError
            && authProbeError.status !== 401
          ) {
            console.warn("Unexpected auth probe error", authProbeError);
          }
          setAuthenticated(false);
          setAuthUsername(null);
        }
      } catch (modeError) {
        if (!active || abort.signal.aborted) return;
        console.error("Failed to probe auth mode", modeError);
        setMode("unknown");
        setBasicEnabled(false);
        setOidcEnabled(false);
        setAuthenticated(false);
        setAuthUsername(null);
        setBootError(
          formatAuthError(modeError, "Could not reach authentication service"),
        );
      } finally {
        if (active) setChecking(false);
      }
    };

    void boot();
    return () => {
      active = false;
      abort.abort();
    };
  }, [bootGeneration]);

  const submit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setError(null);
      if (!username.trim() || !password) {
        setError("Please enter username and password.");
        return;
      }
      setBasicAuthCredentials(username.trim(), password);
      try {
        const me = await apiGet<{ username?: string | null }>("/auth/me");
        setAuthenticated(true);
        setAuthUsername(
          typeof me.username === "string" && me.username ? me.username : username.trim(),
        );
        setPassword("");
      } catch (submitError) {
        console.error("Basic login failed", submitError);
        clearBasicAuthCredentials();
        setAuthenticated(false);
        setAuthUsername(null);
        setError(formatAuthError(submitError, "Login failed"));
      }
    },
    [username, password],
  );

  const logout = useCallback(async () => {
    clearBasicAuthCredentials();
    // Tell the backend to wipe the session cookie too. Fire-and-forget:
    // we still log out client-side even if the request fails.
    try {
      await apiPost("/auth/logout");
    } catch (logoutError) {
      console.warn("Logout request failed", logoutError);
    }
    setAuthenticated(mode === "none");
    setAuthUsername(null);
    setPassword("");
    setError(null);
  }, [mode]);

  const contextValue = useMemo<AuthSessionContextValue>(
    () => ({
      mode: mode === "unknown" ? "none" : mode,
      authenticated,
      username: authUsername,
      logout,
    }),
    [mode, authenticated, authUsername, logout],
  );

  if (checking || mode === "unknown") {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <AuthScreen>
          {bootError ? (
            <Card className="w-full max-w-md border-border/60 bg-card/85 shadow-2xl backdrop-blur">
              <CardHeader className="space-y-1 text-center">
                <CardTitle className="text-xl">Authentication unavailable</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <p className="text-xs text-destructive" role="alert">
                  {bootError}
                </p>
                <Button type="button" className="w-full" onClick={retryBoot}>
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="rounded-md border border-border bg-card/80 px-4 py-3 text-sm text-muted-foreground backdrop-blur">
              Checking authentication mode…
            </div>
          )}
        </AuthScreen>
      </AuthSessionContext.Provider>
    );
  }

  const effectiveMode: SupportedAuthMode = mode;

  if (effectiveMode !== "none" && !authenticated) {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <AuthScreen>
          <Card className="w-full max-w-md border-border/60 bg-card/85 shadow-2xl backdrop-blur">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-xl">Login required</CardTitle>
              <p className="text-xs text-muted-foreground">
                Sign in to dive into your Helm values.
              </p>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              {error ? (
                <p className="text-xs text-destructive" role="alert">
                  {error}
                </p>
              ) : null}
              {basicEnabled ? (
                <form
                  className="flex flex-col gap-3"
                  onSubmit={(event) => void submit(event)}
                >
                  <Input
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder="Username"
                    autoComplete="username"
                    aria-label="Username"
                    autoFocus
                  />
                  <Input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Password"
                    autoComplete="current-password"
                    aria-label="Password"
                  />
                  <Button type="submit" className="w-full">
                    Login
                  </Button>
                </form>
              ) : null}
              {basicEnabled && oidcEnabled ? (
                <div className="text-center text-xs text-muted-foreground">or</div>
              ) : null}
              {oidcEnabled ? <OidcLoginButton /> : null}
            </CardContent>
          </Card>
        </AuthScreen>
      </AuthSessionContext.Provider>
    );
  }

  return (
    <AuthSessionContext.Provider value={contextValue}>
      {children}
    </AuthSessionContext.Provider>
  );
}

/**
 * Login affordance for the BFF flow: a plain anchor that triggers a
 * full top-level navigation to the backend `/api/auth/login` route.
 * The backend then handles the entire OAuth Authorization Code +
 * PKCE roundtrip with Microsoft Entra and bounces the browser back
 * with a signed session cookie set.
 *
 * Rendered as a `<button>` styled to match the basic-auth form for
 * visual consistency; we use `window.location.assign` instead of a
 * real anchor so we can also clean up any stale Basic credentials
 * before kicking off the OIDC flow.
 */
function OidcLoginButton(): JSX.Element {
  return (
    <Button
      type="button"
      variant="secondary"
      className="w-full"
      onClick={() => {
        clearBasicAuthCredentials();
        window.location.assign(apiUrl("/auth/login"));
      }}
    >
      <img
        src="/azure-logo.svg"
        alt=""
        aria-hidden
        className="size-4 rounded-sm"
      />
      Login with Microsoft
    </Button>
  );
}

function formatAuthError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return `${fallback}: ${error.message}`;
  }
  if (error instanceof Error) {
    return `${fallback}: ${error.message}`;
  }
  if (typeof error === "string" && error.trim()) {
    return `${fallback}: ${error}`;
  }
  return fallback;
}

interface AuthScreenProps {
  children: React.ReactNode;
}

/**
 * Full-viewport canvas for the login flow: centers its child and shows
 * the helmdiver logo as a low-opacity watermark in the background, in
 * the same spirit as `EmptyState` inside the ComparisonFrame.
 */
function AuthScreen({ children }: AuthScreenProps): JSX.Element {
  return (
    <div
      data-testid="auth-screen"
      className="fixed inset-0 z-30 flex items-center justify-center overflow-hidden bg-background px-4"
    >
      <img
        src="/helmdiver-logo-1024.png"
        alt=""
        aria-hidden
        className="pointer-events-none absolute inset-0 m-auto size-[36rem] max-w-[80%] opacity-10"
      />
      <div className="relative z-10 flex w-full max-w-md flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <img
            src="/helmdiver-logo-192.png"
            alt=""
            aria-hidden
            className="size-12 rounded-lg shadow-lg"
          />
          <span className="text-lg font-semibold tracking-tight text-foreground">
            helmdiver
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}
